import api from './authService'

export interface VehiculoAPI {
  id_vehiculo: number
  id_organizacion: number
  placa: string
  marca: string | null
  modelo: string | null
  anio: number | null
  vin: string | null
  capacidad_carga_kg: number | null
  tipo_vehiculo: string | null
  estado: string
  fecha_creacion: string
  fecha_actualizacion: string | null
}

export interface Vehiculo {
  id: string
  idOrganizacion: number
  placa: string
  marca: string
  modelo: string
  año: number
  tipo: string
  estado: string
  capacidadCarga: number
  fechaCreacion: string
  fechaActualizacion: string
}

export interface FiltrosVehiculos {
  skip?: number
  limit?: number
}

export interface VehiculoCrear {
  placa: string
  marca?: string
  modelo?: string
  anio?: number
  vin?: string
  capacidad_carga_kg?: number
  tipo_vehiculo?: string
  estado?: string
}

export interface VehiculoActualizar {
  placa?: string
  marca?: string
  modelo?: string
  anio?: number
  vin?: string
  capacidad_carga_kg?: number
  tipo_vehiculo?: string
  estado?: string
}

// Función para normalizar datos del API
const normalizarVehiculo = (vehiculoAPI: VehiculoAPI): Vehiculo => {
  return {
    id: vehiculoAPI.id_vehiculo.toString(),
    idOrganizacion: vehiculoAPI.id_organizacion,
    placa: vehiculoAPI.placa,
    marca: vehiculoAPI.marca || '',
    modelo: vehiculoAPI.modelo || '',
    año: vehiculoAPI.anio || 0,
    tipo: vehiculoAPI.tipo_vehiculo || 'otro',
    estado: vehiculoAPI.estado,
    capacidadCarga: vehiculoAPI.capacidad_carga_kg || 0,
    fechaCreacion: vehiculoAPI.fecha_creacion,
    fechaActualizacion: vehiculoAPI.fecha_actualizacion || ''
  }
}

export const vehiculosService = {
  // Obtener lista de vehículos
  async obtenerVehiculos(filtros?: FiltrosVehiculos, idOrganizacion?: number): Promise<Vehiculo[]> {
    try {
      const params = new URLSearchParams()
      if (filtros?.limit) params.append('limit', filtros.limit.toString())
      if (filtros?.skip) params.append('skip', filtros.skip.toString())

      const response = await api.get(`/api/vehiculos?${params.toString()}`)
      const vehiculos = (response.data as VehiculoAPI[]).map(normalizarVehiculo)

      if (typeof idOrganizacion === 'number') {
        return vehiculos.filter((vehiculo) => vehiculo.idOrganizacion === idOrganizacion)
      }

      return vehiculos
    } catch (error) {
      console.error('Error al obtener vehículos:', error)
      throw error
    }
  },

  // Crear nuevo vehículo
  async crearVehiculo(vehiculo: VehiculoCrear): Promise<Vehiculo> {
    try {
      const response = await api.post('/api/vehiculos', vehiculo)
      return normalizarVehiculo(response.data)
    } catch (error) {
      console.error('Error al crear vehículo:', error)
      throw error
    }
  },

  // Actualizar vehículo
  async actualizarVehiculo(id: string, vehiculo: VehiculoActualizar): Promise<Vehiculo> {
    try {
      const response = await api.put(`/api/vehiculos/${id}`, vehiculo)
      return normalizarVehiculo(response.data)
    } catch (error) {
      console.error('Error al actualizar vehículo:', error)
      throw error
    }
  },

  // Eliminar vehículo
  async eliminarVehiculo(id: string): Promise<void> {
    try {
      await api.delete(`/api/vehiculos/${id}`)
    } catch (error) {
      console.error('Error al eliminar vehículo:', error)
      throw error
    }
  },

  // Obtener vehículo por ID
  async obtenerVehiculoPorId(id: string): Promise<Vehiculo> {
    try {
      const response = await api.get(`/api/vehiculos/${id}`)
      return normalizarVehiculo(response.data)
    } catch (error) {
      console.error('Error al obtener vehículo:', error)
      throw error
    }
  },

  // Cambiar estado del vehículo
  async cambiarEstado(id: string, estado: string): Promise<Vehiculo> {
    try {
      const response = await api.put(`/api/vehiculos/${id}`, { estado })
      return normalizarVehiculo(response.data)
    } catch (error) {
      console.error('Error al cambiar estado del vehículo:', error)
      throw error
    }
  }
}
